import { Map, MapEvent } from 'ol';
import { unByKey } from 'ol/Observable';
import { EventsKey } from 'ol/events';
import Layer from 'ol/layer/Layer';
import { createLogger } from 'services/debugLogger';

const logger = createLogger('layerLifecycle');

/**
 * Disposes a layer (and its source) that has ALREADY been removed from the map, but only after
 * a frame has been rendered WITHOUT it. Layer.dispose() nulls `layerState.layer`, and the map
 * keeps using the last frame's layer states for pointer events until a new frame is rendered:
 * disposing earlier makes the next pointermove throw
 * "Cannot read properties of null (reading 'hasRenderer')".
 *
 * Neither "right after removing" nor "on the next postrender" is enough: the layer can be
 * removed from inside a render (e.g. a cache-hit image fires `imageloadend` synchronously while
 * the map is rendering), and then the very next postrender belongs to that same frame, which
 * still contains the layer. So each postrender is checked against the frame's layer states.
 * If the map never renders again the layer is simply left to the garbage collector.
 */
function disposeLayerAfterRender(map: Map, layer: Layer) {
  let key: EventsKey | undefined;

  const run = (event: MapEvent) => {
    const layerStates = event.frameState?.layerStatesArray;
    if (layerStates?.some((state) => state.layer === layer)) return; // frame still shows it
    if (key) unByKey(key);
    key = undefined;
    try {
      layer.getSource()?.dispose();
      layer.dispose();
    } catch (e) {
      logger.warn('Error disposing layer', e);
    }
  };

  key = map.on('postrender', run);
  map.render();
}

export default disposeLayerAfterRender;
